import {formatMebibytes, MEMORY_KIND_LABEL, type MemoryKind, type TaskMemoryStats} from "@cm/job-nodes/memory-stats"

// Presentation of a task run's memory statistics (contract, parsing and formatting: @cm/job-nodes/memory-stats).

/** A compact pill (icon + text, with an explanation on hover); shape-compatible with the job-task-details chips. */
type MemoryStatsChip = {icon: string; text: string; title?: string}

/** What "~" means, for whoever hovers over an approximate reading. */
export const SAMPLED_EXPLANATION = "~ approximate: read periodically while the task ran, so a brief spike may be missed"

const SYSTEM_SAMPLED = `${SAMPLED_EXPLANATION}; it covers the whole worker, which may include memory kept from earlier tasks`
const GPU_SAMPLED = `${SAMPLED_EXPLANATION}; it counts every process on the GPU`

const OUT_OF_MEMORY_SENTENCE: Record<MemoryKind, string> = {
    system: "The task was killed for running out of system memory.",
    gpu: "The task ran out of GPU memory.",
}

// "15.4 GiB used of 32 GiB", or just "15.4 GiB used" when the limit is unknown.
const usedOfAvailable = (usedMb: number, availableMb: number | undefined): string =>
    `${formatMebibytes(usedMb)} used${availableMb === undefined ? "" : ` of ${formatMebibytes(availableMb)}`}`

/**
 * The three things a reader wants to know, as pills under an event: system RAM used of available, GPU
 * memory used of available, and whether the task ran out of memory. A sampled reading is shown as
 * approximate ("~") and explained on hover; GPU memory is always sampled.
 */
export function memoryStatsChips(stats: TaskMemoryStats): MemoryStatsChip[] {
    const chips: MemoryStatsChip[] = []
    // The limits a runner found when it started the task (Running event): what was available, nothing used yet.
    if (stats.limits?.systemMb !== undefined) chips.push({icon: "far fa-memory", text: `System RAM ${formatMebibytes(stats.limits.systemMb)} available`})
    if (stats.limits?.gpuMb !== undefined) chips.push({icon: "far fa-microchip", text: `GPU ${formatMebibytes(stats.limits.gpuMb)} available`})
    if (stats.system) {
        const sampled = stats.system.precision === "sampled"
        chips.push({
            icon: "far fa-memory",
            text: `System RAM ${sampled ? "~" : ""}${usedOfAvailable(stats.system.usedMb, stats.system.availableMb)}`,
            ...(sampled && {title: SYSTEM_SAMPLED}),
        })
    }
    if (stats.gpu) chips.push({icon: "far fa-microchip", text: `GPU ~${usedOfAvailable(stats.gpu.usedMb, stats.gpu.availableMb)}`, title: GPU_SAMPLED})
    if (stats.oom) {
        chips.push({
            icon: "far fa-triangle-exclamation",
            text: `Out of memory (${MEMORY_KIND_LABEL[stats.oom.kind]})`,
            title: OUT_OF_MEMORY_SENTENCE[stats.oom.kind],
        })
    }
    return chips
}

/**
 * The same facts as plain lines, for tooltips — with the plain sentence for an OOM, and what "~" means when
 * a reading is approximate.
 */
export function memoryStatsLines(stats: TaskMemoryStats): string[] {
    const sampled = stats.system?.precision === "sampled" || stats.gpu !== undefined
    return [
        ...memoryStatsChips(stats).map((chip) => chip.text),
        ...(stats.oom ? [OUT_OF_MEMORY_SENTENCE[stats.oom.kind]] : []),
        ...(sampled ? [SAMPLED_EXPLANATION] : []),
    ]
}
